import React from 'react';
import { motion } from 'framer-motion';
import { 
  Rocket, 
  Target, 
  TrendingUp, 
  Users, 
  Globe, 
  Award,
  Sparkles,
  Zap,
  Star,
  Calendar,
  CheckCircle,
  ArrowRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Journey = () => {
  const milestones = [
    {
      year: "2023",
      quarter: "Q1-Q2",
      title: "Foundation & Vision",
      description: "Idea conceptualization, market research, and strategic planning. Identifying gaps in educational and startup digital solutions.",
      icon: <Rocket className="w-6 h-6" />,
      gradient: "from-blue-500 to-cyan-400",
      achievements: [
        "Market analysis completed",
        "Core team formation",
        "Service framework established"
      ],
      stats: "0 → 5 Team Members"
    },
    {
      year: "2023",
      quarter: "Q3-Q4",
      title: "First Breakthrough",
      description: "Development of initial MVP, securing first pilot projects with educational institutions.",
      icon: <Target className="w-6 h-6" />,
      gradient: "from-purple-500 to-pink-400",
      achievements: [
        "First 3 client projects",
        "Tech stack finalized",
        "Brand identity launch"
      ],
      stats: "3 Pilot Projects"
    },
    {
      year: "2024",
      quarter: "Q1-Q2",
      title: "Growth & Recognition",
      description: "Expanding client portfolio, receiving industry recognition, and establishing partnerships.",
      icon: <TrendingUp className="w-6 h-6" />,
      gradient: "from-green-500 to-emerald-400",
      achievements: [
        "50+ projects delivered",
        "15+ countries served",
        "Industry awards received"
      ],
      stats: "250% YoY Growth"
    },
    {
      year: "2024",
      quarter: "Q3-Q4",
      title: "Scale & Innovation",
      description: "Implementing automation, AI integration, and expanding service offerings.",
      icon: <Zap className="w-6 h-6" />,
      gradient: "from-amber-500 to-orange-400",
      achievements: [
        "AI-powered solutions launched",
        "Process automation implemented",
        "Team expanded to 25+"
      ],
      stats: "500+ Projects"
    },
    {
      year: "2025",
      quarter: "Q1-Q2",
      title: "Global Expansion",
      description: "Establishing international presence, new verticals, and strategic partnerships.",
      icon: <Globe className="w-6 h-6" />,
      gradient: "from-indigo-500 to-blue-400",
      achievements: [
        "Global offices established",
        "New service verticals launched",
        "Enterprise partnerships formed"
      ],
      stats: "15+ Countries"
    },
    {
      year: "2025+",
      quarter: "Future Vision",
      title: "Industry Leadership",
      description: "Becoming the go-to digital solution provider for education and startups worldwide.",
      icon: <Star className="w-6 h-6" />,
      gradient: "from-rose-500 to-pink-400",
      achievements: [
        "Market leadership position",
        "AI-first solutions",
        "Global impact initiatives"
      ],
      stats: "Vision 2030"
    }
  ];

  const stats = [
    { value: "500+", label: "Projects", icon: <CheckCircle className="w-5 h-5" /> },
    { value: "250+", label: "Clients", icon: <Users className="w-5 h-5" /> },
    { value: "15+", label: "Countries", icon: <Globe className="w-5 h-5" /> },
    { value: "99%", label: "Satisfaction", icon: <Award className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-20">
      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-cyan-50/50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <Badge className="mb-6 bg-gradient-to-r from-blue-600 to-cyan-500 text-white">
              <Sparkles className="w-4 h-4 mr-2" />
              Our Evolution
            </Badge>
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              The <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500">Skyreti Journey</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
              From a visionary idea to a global digital solutions provider. Here's our story of growth, innovation, and impact.
            </p>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto mt-12"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                whileHover={{ y: -5 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-blue-100 to-cyan-100 mb-3">
                  <div className="text-blue-600">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-200 via-blue-400 to-cyan-200 transform -translate-x-1/2" />

            {milestones.map((milestone, index) => (
              <motion.div
                key={`${milestone.year}-${index}`}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative mb-12 md:mb-20 ${
                  index % 2 === 0 ? 'md:pr-1/2 md:pl-12' : 'md:pl-1/2 md:pr-12'
                }`}
              >
                {/* Timeline Dot */}
                <div className={`absolute left-0 md:left-1/2 w-8 h-8 rounded-full border-4 border-white shadow-lg transform -translate-x-1/2 z-10 ${
                  index <= 2 ? 'bg-gradient-to-r from-blue-500 to-cyan-400' : 
                  index <= 4 ? 'bg-gradient-to-r from-green-500 to-emerald-400' : 
                  'bg-gradient-to-r from-purple-500 to-pink-400'
                }`}>
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    {milestone.icon}
                  </div>
                </div>

                {/* Content Card */}
                <div className={`ml-12 md:ml-0 ${
                  index % 2 === 0 ? 'md:text-right md:pr-8' : 'md:text-left md:pl-8'
                }`}>
                  <Card className="border-none shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group">
                    <div className={`absolute top-0 right-0 w-24 h-24 overflow-hidden rounded-tr-lg ${
                      index % 2 === 0 ? 'md:left-0 md:right-auto' : ''
                    }`}>
                      <div className={`absolute -right-12 -top-12 w-32 h-32 bg-gradient-to-br ${milestone.gradient} opacity-10 group-hover:opacity-20 transition-opacity`} />
                    </div>

                    <CardContent className="p-6">
                      {/* Header */}
                      <div className={`flex items-center gap-3 mb-4 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                        <Badge className={`bg-gradient-to-r ${milestone.gradient} text-white`}>
                          <Calendar className="w-3 h-3 mr-1" />
                          {milestone.year} • {milestone.quarter}
                        </Badge>
                        <div className="text-sm font-semibold bg-gradient-to-r bg-clip-text text-transparent from-gray-600 to-gray-400">
                          {milestone.stats}
                        </div>
                      </div>

                      {/* Title */}
                      <h3 className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                        {milestone.title}
                      </h3>

                      {/* Description */}
                      <p className="text-gray-600 mb-6 leading-relaxed">
                        {milestone.description}
                      </p>

                      {/* Achievements */}
                      <div className="mb-6">
                        <div className="text-sm font-semibold text-gray-700 mb-3">Key Achievements:</div>
                        <ul className="space-y-2">
                          {milestone.achievements.map((achievement, idx) => (
                            <motion.li
                              key={idx}
                              initial={{ opacity: 0, x: -10 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.1 }}
                              className="flex items-start gap-2"
                            >
                              <div className={`w-2 h-2 rounded-full mt-2 bg-gradient-to-r ${milestone.gradient}`} />
                              <span className="text-gray-600 text-sm">{achievement}</span>
                            </motion.li>
                          ))}
                        </ul>
                      </div>

                      {/* Gradient Border Bottom */}
                      <div className={`h-1 bg-gradient-to-r ${milestone.gradient} rounded-full opacity-0 group-hover:opacity-100 transition-opacity`} />
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Current Status */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Badge className="mb-6 bg-black text-white">
              <Sparkles className="w-4 h-4 mr-2" />
              Today at Skyreti
            </Badge>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">
              Building the Future, Today
            </h2>

            <div className="grid md:grid-cols-3 gap-8 mb-12">
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Zap className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Active Projects</h3>
                <p className="text-gray-600 text-sm">50+ ongoing digital transformations</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Users className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Team Members</h3>
                <p className="text-gray-600 text-sm">25+ experts across domains</p>
              </div>
              
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-4 mx-auto">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Global Reach</h3>
                <p className="text-gray-600 text-sm">Serving clients across 15+ countries</p>
              </div>
            </div>

            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              We continue to innovate and push boundaries in digital education and startup solutions, with a commitment to excellence and client success.
            </p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <button className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl transition-all">
                Join Our Journey
                <ArrowRight className="w-5 h-5" />
              </button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Future Vision */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  viewport={{ once: true }}
>
  <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6">
    Looking{" "}
    <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
      Ahead
    </span>
  </h2>

  <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-3xl p-8 md:p-12 mb-8">
    <Star className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
    <h3 className="text-2xl font-bold mb-4">Vision 2030</h3>
    <p className="text-lg text-gray-300 mb-6">
      To become the world's most trusted digital transformation partner for
      education and startups, impacting 1 million+ lives through technology.
    </p>

    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
      {[
        { label: "Global Presence", value: "50+ Countries" },
        { label: "Team Growth", value: "500+ Members" },
        { label: "Projects", value: "10,000+" },
        { label: "Social Impact", value: "1M+ Lives" }
      ].map((goal, idx) => (
        <div key={idx} className="text-center">
          <div className="text-xl font-bold text-white mb-1">
            {goal.value}
          </div>
          <div className="text-gray-400">{goal.label}</div>
        </div>
      ))}
    </div>
  </div>

  <p className="text-gray-600 mb-8">
    Every milestone is a new beginning. Join us as we continue to shape the
    future of digital transformation.
  </p>

  {/* CTA → Home Contact Section */}
  <motion.a
    href="/#contact"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold rounded-xl hover:shadow-xl transition-all"
  >
    Get in Touch
    <ArrowRight className="w-5 h-5" />
  </motion.a>
</motion.div>

        </div>
      </section>
    </div>
  );
};

export default Journey;